import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Skeleton } from '../ui/skeleton';
import { Save, Loader2 } from 'lucide-react';
import { InvoiceAPI, InvoiceConfig } from '../../lib/api';

interface InvoiceConfigModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSaved: () => void;
}

const InvoiceConfigModal: React.FC<InvoiceConfigModalProps> = ({ isOpen, onClose, onSaved }) => {
    const [config, setConfig] = useState<InvoiceConfig | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) {
            fetchConfig();
        }
    }, [isOpen]);

    const fetchConfig = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await InvoiceAPI.getConfig();
            if (response.success) {
                setConfig(response.data);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load configuration');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!config) return;
        setSaving(true);
        setError(null);
        try {
            const response = await InvoiceAPI.updateConfig(config);
            if (response.success) {
                onSaved();
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save configuration');
        } finally {
            setSaving(false);
        }
    };

    const updateField = (field: keyof InvoiceConfig, value: any) => {
        if (!config) return;
        setConfig({ ...config, [field]: value });
    };

    const updateAddress = (field: keyof InvoiceConfig['companyAddress'], value: string) => {
        if (!config) return;
        setConfig({
            ...config,
            companyAddress: { ...config.companyAddress, [field]: value }
        });
    };

    const updateBankDetails = (field: keyof InvoiceConfig['bankDetails'], value: string) => {
        if (!config) return;
        setConfig({
            ...config,
            bankDetails: { ...config.bankDetails, [field]: value }
        });
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Invoice Configuration</DialogTitle>
                    <DialogDescription>
                        Configure company details, bank information, and invoice styling.
                    </DialogDescription>
                </DialogHeader>

                {error && (
                    <div className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm">
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="space-y-4">
                        <Skeleton className="h-32 w-full" />
                        <Skeleton className="h-32 w-full" />
                        <Skeleton className="h-32 w-full" />
                    </div>
                ) : config ? (
                    <div className="space-y-6">
                        {/* Company Details */}
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-lg">Company Details</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Company Name</Label>
                                        <Input
                                            value={config.companyName}
                                            onChange={(e) => updateField('companyName', e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>GSTIN</Label>
                                        <Input
                                            value={config.companyGstin}
                                            onChange={(e) => updateField('companyGstin', e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Phone</Label>
                                        <Input
                                            value={config.companyPhone}
                                            onChange={(e) => updateField('companyPhone', e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Email</Label>
                                        <Input
                                            value={config.companyEmail}
                                            onChange={(e) => updateField('companyEmail', e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label>Address</Label>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                        <Input
                                            placeholder="Street"
                                            value={config.companyAddress?.street || ''}
                                            onChange={(e) => updateAddress('street', e.target.value)}
                                        />
                                        <Input
                                            placeholder="City"
                                            value={config.companyAddress?.city || ''}
                                            onChange={(e) => updateAddress('city', e.target.value)}
                                        />
                                        <Input
                                            placeholder="State"
                                            value={config.companyAddress?.state || ''}
                                            onChange={(e) => updateAddress('state', e.target.value)}
                                        />
                                        <Input
                                            placeholder="Pincode"
                                            value={config.companyAddress?.pincode || ''}
                                            onChange={(e) => updateAddress('pincode', e.target.value)}
                                        />
                                        <Input
                                            placeholder="Country"
                                            value={config.companyAddress?.country || ''}
                                            onChange={(e) => updateAddress('country', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Bank Details */}
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-lg">Bank Details</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Bank Name</Label>
                                        <Input
                                            value={config.bankDetails?.bankName || ''}
                                            onChange={(e) => updateBankDetails('bankName', e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Account Name</Label>
                                        <Input
                                            value={config.bankDetails?.accountName || ''}
                                            onChange={(e) => updateBankDetails('accountName', e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Account Number</Label>
                                        <Input
                                            value={config.bankDetails?.accountNumber || ''}
                                            onChange={(e) => updateBankDetails('accountNumber', e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>IFSC Code</Label>
                                        <Input
                                            value={config.bankDetails?.ifscCode || ''}
                                            onChange={(e) => updateBankDetails('ifscCode', e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Branch</Label>
                                        <Input
                                            value={config.bankDetails?.branch || ''}
                                            onChange={(e) => updateBankDetails('branch', e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>UPI ID</Label>
                                        <Input
                                            value={config.upiId || ''}
                                            onChange={(e) => updateField('upiId', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Styling & Content */}
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-lg">Styling & Content</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Primary Color</Label>
                                        <div className="flex gap-2">
                                            <Input
                                                type="color"
                                                value={config.primaryColor}
                                                onChange={(e) => updateField('primaryColor', e.target.value)}
                                                className="w-14 h-10 p-1"
                                            />
                                            <Input
                                                value={config.primaryColor}
                                                onChange={(e) => updateField('primaryColor', e.target.value)}
                                                className="flex-1"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Footer Note</Label>
                                        <Input
                                            value={config.footerNote}
                                            onChange={(e) => updateField('footerNote', e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label>Terms and Conditions (one per line)</Label>
                                    <Textarea
                                        rows={4}
                                        value={config.termsAndConditions?.join('\n') || ''}
                                        onChange={(e) => updateField('termsAndConditions', e.target.value.split('\n').filter(Boolean))}
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Save Button */}
                        <div className="flex justify-end pt-4">
                            <Button onClick={handleSave} disabled={saving}>
                                {saving ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                    <Save className="h-4 w-4 mr-2" />
                                )}
                                Save Configuration
                            </Button>
                        </div>
                    </div>
                ) : null}
            </DialogContent>
        </Dialog>
    );
};

export default InvoiceConfigModal;
